import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { authStore } from "@/features/auth/stores";
import { esgService } from "../services";
import {
  getDisclosureFrequency,
  getDisclosureIdentifier,
  normalizeDisclosure,
} from "../utils/disclosure-utils";
import {
  extractFrameworks,
  extractDisclosures,
  extractTargets,
  extractDeletedCount,
  extractDataPayload,
  normalizeFramework,
} from "../utils/api-normalizers";
import { storage } from "../utils/storage";

const STORAGE_KEYS = {
  framework: "nxstart_kpi_framework",
  year: "nxstart_kpi_year",
  category: "nxstart_kpi_category",
};

const DEFAULT_FRAMEWORKS = [
  "JJSEA",
  "SGX",
  "BURSA",
  "TCFD",
  "GRI",
  "IFRS-1",
  "IFRS-2",
];

/**
 * KPI Targets Store
 * Manages quantitative disclosure targets and frameworks
 */
class KpiTargetsStore {
  // State
  targets = new Map();
  changedTargets = new Set();
  disclosures = new Map();
  frameworks = [];

  // Loading states
  loadingFrameworks = false;
  loadingDisclosures = false;
  loadingTargets = false;
  savingTargets = false;
  seedingTargets = false;
  resettingTargets = false;

  // Filter state
  selectedFramework = "All";
  selectedYear = new Date().getFullYear();
  activeCategory = "Environment";
  targetSearchTerm = "";

  // Organization context
  orgId = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: false });
    this.hydrateSelections();
  }

  /**
   * Restore filter selections from localStorage
   */
  hydrateSelections = () => {
    const storedFramework = storage.read(STORAGE_KEYS.framework);
    const storedYear = storage.read(STORAGE_KEYS.year);
    const storedCategory = storage.read(STORAGE_KEYS.category);

    if (storedFramework) {
      this.selectedFramework = storedFramework;
    }
    if (storedYear && !Number.isNaN(Number(storedYear))) {
      this.selectedYear = Number(storedYear);
    }
    if (storedCategory) {
      this.activeCategory = storedCategory;
    }
  };

  // Computed properties
  get isEnvOnly() {
    return authStore.user?.accountType === "esg_env_only";
  }

  get hasDisclosuresLoaded() {
    return this.disclosures.size > 0;
  }

  get allTargets() {
    return Array.from(this.targets.values());
  }

  get hasChangedTargets() {
    return this.changedTargets.size > 0;
  }

  // Filter setters
  setSelectedFramework = (framework) => {
    this.selectedFramework = framework || "All";
    storage.write(STORAGE_KEYS.framework, this.selectedFramework);
  };

  setSelectedYear = (year) => {
    const next = Number(year);
    if (!Number.isFinite(next)) return;
    this.selectedYear = next;
    storage.write(STORAGE_KEYS.year, next);
  };

  setActiveCategory = (category) => {
    this.activeCategory = category || "All";
    storage.write(STORAGE_KEYS.category, this.activeCategory);
  };

  setTargetSearchTerm = (term) => {
    this.targetSearchTerm = term || "";
  };

  setOrg = (orgId) => {
    this.orgId = orgId || null;
  };

  // Target key management
  getTargetKey = (disclosureId, year = null) => {
    const targetYear = year || this.selectedYear;
    return `${disclosureId}_${targetYear}`;
  };

  getTarget = (disclosureId) => {
    const key = this.getTargetKey(disclosureId);
    return this.targets.get(key);
  };

  /**
   * Create default target structure for a disclosure
   */
  getDefaultTarget = (disclosure) => {
    const frequency = getDisclosureFrequency(disclosure);
    const disclosureId = getDisclosureIdentifier(disclosure);
    const target = {
      disclosureCode: disclosureId,
      disclosureTitle: disclosure.title,
      framework: disclosure.framework,
      category: disclosure.category,
      unit: disclosure.unit,
      annual: 0,
      quarterly: null,
      monthly: null,
      year: this.selectedYear,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    if (frequency === "quarterly") {
      target.quarterly = [0, 0, 0, 0];
    } else if (frequency === "monthly") {
      target.monthly = Array(12).fill(0);
    }

    return target;
  };

  /**
   * Set annual target value and distribute to periods
   */
  setAnnualTarget = (disclosure, value) => {
    const disclosureId = getDisclosureIdentifier(disclosure);
    const targetKey = this.getTargetKey(disclosureId);
    const frequency = getDisclosureFrequency(disclosure);
    const annualValue = parseFloat(value) || 0;
    const target =
      this.getTarget(disclosureId) || this.getDefaultTarget(disclosure);

    // Distribute annual value to periods
    if (frequency === "quarterly") {
      const quarterlyValue = annualValue / 4;
      target.quarterly = [
        quarterlyValue,
        quarterlyValue,
        quarterlyValue,
        quarterlyValue,
      ];
    } else if (frequency === "monthly") {
      const monthlyValue = annualValue / 12;
      target.monthly = Array(12).fill(monthlyValue);
    }

    target.annual = annualValue;
    target.year = this.selectedYear;
    target.updatedAt = new Date().toISOString();

    this.targets.set(targetKey, target);
    this.changedTargets.add(targetKey);
  };

  /**
   * Set target value for a specific period and recalculate annual
   */
  setPeriodTarget = (disclosure, periodIndex, value) => {
    const disclosureId = getDisclosureIdentifier(disclosure);
    const targetKey = this.getTargetKey(disclosureId);
    const frequency = getDisclosureFrequency(disclosure);
    const target =
      this.targets.get(targetKey) || this.getDefaultTarget(disclosure);
    const periodValue = parseFloat(value) || 0;

    if (frequency === "quarterly" && target.quarterly) {
      target.quarterly[periodIndex] = periodValue;
      target.annual = target.quarterly.reduce((sum, val) => sum + val, 0);
    } else if (frequency === "monthly" && target.monthly) {
      target.monthly[periodIndex] = periodValue;
      target.annual = target.monthly.reduce((sum, val) => sum + val, 0);
    }

    target.updatedAt = new Date().toISOString();
    this.targets.set(targetKey, target);
    this.changedTargets.add(targetKey);
  };

  /**
   * Clear a single target
   */
  clearTarget = async (disclosureId) => {
    const targetKey = this.getTargetKey(disclosureId);
    const existing = this.targets.get(targetKey);
    if (!existing) return;

    this.targets.delete(targetKey);
    this.changedTargets.delete(targetKey);

    const data = await esgService.deleteQuantitativeTargets({
      year: this.selectedYear,
      masterMetricId: disclosureId,
    });

    if (!data) {
      // Restore on failure
      this.targets.set(targetKey, existing);
      showToast.error("Failed to delete target.");
    }
  };

  /**
   * Clear all targets matching current filters
   */
  clearAllTargets = async () => {
    runInAction(() => {
      this.resettingTargets = true;
    });

    const params = { year: this.selectedYear };
    if (this.selectedFramework !== "All") {
      params.framework = this.selectedFramework;
    }
    if (this.activeCategory !== "All") {
      params.category = this.activeCategory;
    }

    try {
      const data = await esgService.deleteQuantitativeTargets(params);

      if (!data) {
        throw new Error("Failed to delete targets.");
      }

      // Remove matching targets from local state
      const keysToDelete = [];
      this.targets.forEach((target, key) => {
        if (target.year !== this.selectedYear) return;
        if (params.framework && target.framework !== params.framework) return;
        if (params.category && target.category !== params.category) return;
        keysToDelete.push(key);
      });

      keysToDelete.forEach((key) => this.targets.delete(key));
      this.changedTargets.clear();

      const deletedCount = extractDeletedCount(data);
      showToast.success(`Deleted ${deletedCount} targets.`);

      return true;
    } catch (error) {
      showToast.error(error?.message || "Failed to delete targets.");
      return false;
    } finally {
      runInAction(() => {
        this.resettingTargets = false;
      });
    }
  };

  /**
   * Load frameworks from API
   */
  loadFrameworks = async () => {
    if (this.loadingFrameworks || this.frameworks.length > 0) return;

    runInAction(() => {
      this.loadingFrameworks = true;
    });

    try {
      const data = await esgService.getFrameworks({ throwOnError: false });

      if (!data) {
        throw new Error("Failed to load frameworks.");
      }

      const list = extractFrameworks(data);

      runInAction(() => {
        if (Array.isArray(list) && list.length > 0) {
          this.frameworks = list.map(normalizeFramework).filter(Boolean);
        } else {
          this.frameworks = DEFAULT_FRAMEWORKS.map((code) => ({
            code,
            name: code,
          }));
        }
      });
    } catch (error) {
      runInAction(() => {
        this.frameworks = DEFAULT_FRAMEWORKS.map((code) => ({
          code,
          name: code,
        }));
      });
      showToast.error(error?.message || "Failed to load frameworks.");
    } finally {
      runInAction(() => {
        this.loadingFrameworks = false;
      });
    }
  };

  /**
   * Load disclosures from API
   */
  loadDisclosuresFromApi = async (framework = "All", category = "All") => {
    if (this.loadingDisclosures) return;

    // Return early if already loaded
    if (framework === "All" && this.disclosures.size > 0) {
      return this.getAllQuantitativeDisclosures().length;
    }
    if (framework !== "All" && this.disclosures.has(framework)) {
      return this.disclosures.get(framework)?.length || 0;
    }

    runInAction(() => {
      this.loadingDisclosures = true;
    });

    try {
      const resolvedCategory = this.isEnvOnly ? "Environment" : category;
      const data = await esgService.getDisclosures({
        framework,
        category: resolvedCategory,
        page: 1,
        limit: 10000,
        throwOnError: false,
      });

      if (!data) {
        throw new Error("Failed to load disclosures.");
      }

      const payload = extractDisclosures(data);

      runInAction(() => {
        if (framework !== "All") {
          this.disclosures.set(framework, payload);
        } else {
          // Group by framework
          this.disclosures.clear();
          payload.forEach((item) => {
            const fw = item.framework;
            if (!fw) return;
            if (!this.disclosures.has(fw)) {
              this.disclosures.set(fw, []);
            }
            this.disclosures.get(fw).push(item);
          });
        }
      });

      return payload.length;
    } catch (error) {
      showToast.error(error?.message || "Failed to load disclosures.");
      return 0;
    } finally {
      runInAction(() => {
        this.loadingDisclosures = false;
      });
    }
  };

  /**
   * Get disclosures for a specific framework
   */
  getDisclosuresForFramework = (framework) => {
    return this.disclosures.get(framework) || [];
  };

  /**
   * Get quantitative disclosures for a framework
   */
  getQuantitativeDisclosures = (framework = "All") => {
    if (framework === "All") {
      return this.getAllQuantitativeDisclosures();
    }
    return this.getDisclosuresForFramework(framework)
      .filter((d) => d.isQuantitative)
      .map(normalizeDisclosure);
  };

  /**
   * Get all quantitative disclosures across all frameworks
   */
  getAllQuantitativeDisclosures = () => {
    const all = [];
    this.disclosures.forEach((list) => {
      list
        .filter((d) => d.isQuantitative)
        .forEach((item) => {
          all.push(normalizeDisclosure(item));
        });
    });
    return all;
  };

  /**
   * Get quantitative disclosures filtered by framework and category
   */
  getQuantitativeDisclosuresByCategory = (framework, category) => {
    const disclosures =
      framework === "All"
        ? this.getAllQuantitativeDisclosures()
        : this.getQuantitativeDisclosures(framework);

    if (category === "All") return disclosures;
    return disclosures.filter((d) => d.category === category);
  };

  /**
   * Get unique categories for a framework
   */
  getCategories = (framework = "All") => {
    if (framework === "All") {
      const all = new Set();
      this.disclosures.forEach((list) => {
        list.forEach((item) => {
          if (item.category) all.add(item.category);
        });
      });
      return Array.from(all);
    }

    const categories = new Set();
    this.getDisclosuresForFramework(framework).forEach((item) => {
      if (item.category) categories.add(item.category);
    });
    return Array.from(categories);
  };

  /**
   * Get disclosure count by framework
   */
  getDisclosureCountByFramework = () => {
    const counts = {};
    this.disclosures.forEach((list, framework) => {
      counts[framework] = list.filter((d) => d.isQuantitative).length;
    });
    counts.All = this.getAllQuantitativeDisclosures().length;
    return counts;
  };

  /**
   * Load targets from API
   */
  loadTargetsFromApi = async (year, framework = "All", category = "All") => {
    runInAction(() => {
      this.loadingTargets = true;
    });

    try {
      const resolvedCategory = this.isEnvOnly ? "Environment" : category;
      const data = await esgService.getQuantitativeTargets({
        year,
        framework,
        category: resolvedCategory,
        page: 1,
        limit: 1000,
        throwOnError: false,
      });

      if (!data) {
        throw new Error("Failed to load targets.");
      }

      const targets = extractTargets(data);

      // Clear existing targets for this year
      const keysToDelete = [];
      this.targets.forEach((target, key) => {
        if (target.year === year) {
          keysToDelete.push(key);
          this.changedTargets.delete(key);
        }
      });
      keysToDelete.forEach((key) => this.targets.delete(key));

      // Add loaded targets
      targets.forEach((target) => {
        const key = `${target.masterMetricId}_${year}`;
        const resolvedFramework =
          framework !== "All"
            ? framework
            : target.applicableFrameworks?.[0] || target.framework || "All";

        this.targets.set(key, {
          disclosureCode: target.masterMetricId,
          disclosureTitle: target.metricName,
          framework: resolvedFramework,
          category: target.category,
          unit: target.unit,
          annual: target.annual,
          quarterly: target.quarterly,
          monthly: target.monthly,
          year,
          notes: target.notes || "",
          createdAt: target.createdAt,
          updatedAt: target.updatedAt,
        });
      });

      return targets.length;
    } catch (error) {
      showToast.error(error?.message || "Failed to load targets.");
      return 0;
    } finally {
      runInAction(() => {
        this.loadingTargets = false;
      });
    }
  };

  /**
   * Save changed targets to API
   */
  saveTargetsToApi = async () => {
    if (!this.hasChangedTargets) {
      throw new Error("No changes to save.");
    }

    runInAction(() => {
      this.savingTargets = true;
    });

    try {
      const targetYear = this.selectedYear;
      const apiTargets = Array.from(this.changedTargets)
        .map((key) => this.targets.get(key))
        .filter((target) => target && target.year === targetYear)
        .map((target) => ({
          masterMetricId: target.disclosureCode,
          metricName: target.disclosureTitle,
          category: target.category,
          unit: target.unit,
          applicableFrameworks: [target.framework],
          annual: target.annual,
          quarterly: target.quarterly,
          monthly: target.monthly,
          notes: target.notes || "",
        }));

      if (apiTargets.length === 0) {
        throw new Error("No changes to save.");
      }

      const data = await esgService.bulkSaveQuantitativeTargets({
        year: targetYear,
        targets: apiTargets,
      });

      this.changedTargets.clear();
      return extractDataPayload(data);
    } finally {
      runInAction(() => {
        this.savingTargets = false;
      });
    }
  };

  /**
   * Seed demo targets
   */
  seedTargets = async ({ year, framework, category }) => {
    runInAction(() => {
      this.seedingTargets = true;
    });

    try {
      const data = await esgService.seedQuantitativeTargets({
        year,
        framework,
        category,
      });

      // Reload targets after seeding
      await this.loadTargetsFromApi(
        this.selectedYear,
        this.selectedFramework,
        this.activeCategory,
      );

      return extractDataPayload(data);
    } finally {
      runInAction(() => {
        this.seedingTargets = false;
      });
    }
  };
}

export const kpiTargetsStore = new KpiTargetsStore();
